import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker, message, Popover, Select, Tooltip } from 'antd';
import { Button, Row, Col, Modal, Input, Spin } from 'antd';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import api from '@/api/tasker';
import Pagination from '@/components/pagination';
import { defaultInfo } from '@/typings/allType';
import { categoryType, setChatUser } from '@/store/stateSlice';
import { useDispatch } from 'react-redux';
import publicMethod, { disabledDate } from '@/utils/publicMethod';
import { useActivate, useAliveController } from 'react-activation';
import useDownLoadCSV from '@/hook/useDownLoadCSV';
import { onlineOptions, stateOptions } from '@/constants/selectOptions';
import categoriesApi from '@/api/categories';
import b2bCategoriesApi from '@/api/b2bCategories';
import taskerCategoriesApi from '@/api/taskerCategories';
import { calendarIcon, searchIcon, selectIcon } from '@/constants/static';
import format from '@/utils/format';
interface Params {
    pageNum: number;
    pageSize: number;
    startTime: string;
    endTime: string;
    keyword: string;
    states: string;
    users: string;
    categories: string;
}
interface TaskerCategoryOption {
    taskerCategoryId: number | string;
    categoryName: string;
}
interface Taskers {
    fullName: string;
    email: string;
    avatarUrl: string;
    lastOnline: string;
    taskerClickCompletionTime: string;
    gender: number; // 0-other 1-male 2-female
    mobile: string;
    tasksCompleted: string | number;
    disabled: boolean;
    taskerId: string;
    taskerCategoryId?: number | string | null;
    taskerCategoryName?: string | null;
    categoryIdList?: string[];
    b2bCategoryIdList?: string[];
}
// Column widths shared by the header and the rows. The editable columns get a floor wide enough for their
// controls to stay readable, and share the spare width with name/email on larger screens.
const COLS = {
    name: '1 1 96px',
    email: '1.5 1 136px',
    mobile: '0 0 92px',
    tasks: '0 0 82px',
    lastTask: '0 0 90px',
    lastOnline: '0 0 90px',
    market: '1 1 136px',
    b2b: '1 1 136px',
    tier: '0.4 1 104px',
    status: '0 0 98px',
    action: '0 0 28px',
};
const rowSelectArrow = <img alt="" src={require('@/assets/images/selected.png')} style={{ width: 14, height: 14 }} />;

interface CategoryMultiSelectProps {
    title: string;
    value: string[];
    options: categoryType[];
    max?: number;
    getPopupContainer: () => HTMLElement;
    onSave: (ids: string[]) => void;
}
// Inline multi-select for a tasker's categories. The cell always stays one line ("First name +2", full list
// in a tooltip), ticks are kept as a draft while the dropdown is open, and the change is saved once on close.
const CategoryMultiSelect = ({ title, value, options, max, getPopupContainer, onSave }: CategoryMultiSelectProps) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<string[]>(value);
    const selected = open ? draft : value;
    const nameById = useMemo(() => new Map(options.map(c => [String(c.categoryId), c.categoryName])), [options]);
    const names = selected.map(id => nameById.get(id)).filter(Boolean) as string[];
    const atMax = max !== undefined && draft.length >= max;

    const handleOpenChange = (next: boolean) => {
        if (next) {
            setDraft(value);
        } else if (draft.length !== value.length || draft.some(id => !value.includes(id))) {
            onSave(draft);
        }
        setOpen(next);
    };

    return (
        <Tooltip
            title={
                !open && names.length ? (
                    <div className="tasker-category-tooltip">
                        {names.map(name => (
                            <div key={name}>{name}</div>
                        ))}
                    </div>
                ) : null
            }
            placement="top"
            mouseEnterDelay={0.3}
        >
            <div className="tasker-row-control">
                <Select
                    className="tasker-row-select tasker-category-multi"
                    popupClassName="tasker-row-dropdown tasker-row-dropdown-multi"
                    mode="multiple"
                    showSearch={false}
                    showArrow
                    maxTagCount={0}
                    maxTagPlaceholder={() =>
                        selected.length > 1 || !names.length ? (
                            <span className="category-summary">
                                {/* Wide cells show "First name +2"; narrow ones switch to "3 selected" (see public.scss). */}
                                <span className="category-summary-name category-summary-wide">
                                    {names[0] ?? `${selected.length} selected`}
                                </span>
                                {names.length > 1 && (
                                    <span className="category-summary-count category-summary-wide">
                                        +{names.length - 1}
                                    </span>
                                )}
                                <span className="category-summary-name category-summary-narrow">
                                    {selected.length} selected
                                </span>
                            </span>
                        ) : (
                            <span className="category-summary">
                                <span className="category-summary-name">{names[0]}</span>
                            </span>
                        )
                    }
                    placeholder="Select"
                    style={{ width: '100%' }}
                    open={open}
                    onDropdownVisibleChange={handleOpenChange}
                    value={selected}
                    onChange={setDraft}
                    options={options.map(c => ({
                        value: String(c.categoryId),
                        label: c.categoryName,
                        disabled: atMax && !draft.includes(String(c.categoryId)),
                    }))}
                    menuItemSelectedIcon={null}
                    dropdownMatchSelectWidth={260}
                    listHeight={232}
                    dropdownRender={menu => (
                        <>
                            <div className="tasker-row-dropdown-header">
                                <span>{title}</span>
                                <span className="tasker-row-dropdown-count">
                                    {max !== undefined ? `${draft.length}/${max}` : draft.length} selected
                                </span>
                            </div>
                            {menu}
                            {/* preventDefault keeps focus in the Select so these clicks don't close it via blur */}
                            <div className="tasker-row-dropdown-footer" onMouseDown={e => e.preventDefault()}>
                                <Button type="link" size="small" disabled={!draft.length} onClick={() => setDraft([])}>
                                    Clear all
                                </Button>
                                <Button type="primary" size="small" onClick={() => handleOpenChange(false)}>
                                    Done
                                </Button>
                            </div>
                        </>
                    )}
                    suffixIcon={rowSelectArrow}
                    getPopupContainer={getPopupContainer}
                />
            </div>
        </Tooltip>
    );
};

const Tasker = props => {
    const link = useNavigate();
    const dispatch = useDispatch();
    const { RangePicker } = DatePicker;
    const [loading, setLoading] = useState(false);
    const [firstLoading, setFirstLoading] = useState(false);
    const [totalRecord, setTotalRecord] = useState(0);
    const [listParams, setListParams] = useState<Params>({
        pageNum: 1,
        pageSize: 10,
        startTime: '',
        endTime: '',
        keyword: '',
        states: 'All',
        users: 'All',
        categories: 'All',
    });
    const [taskNum, setTaskNum] = useState('0');
    const [taskTotal, setTaskTotal] = useState('0.00');
    const [tableData, setTableData] = useState<Taskers[]>([]);
    const inputChange = e => {
        debouncedSave({ ...listParams, keyword: e.target.value, pageNum: 1 });
    };
    const debouncedSave = useCallback(
        debounce(paramsValues => setListParams(paramsValues), 1500),
        []
    );
    async function getTaskers() {
        console.log('getTaskers');
        const { pageNum } = listParams;
        setLoading(true);
        if (pageNum === 1) {
            setFirstLoading(true);
        }
        try {
            const res = await api.getTaskerList(listParams);

            const list = res.data.records.map(item => {
                return Object.assign(item, {
                    createTime: item.createTime ? window.utils.formatTimeMDY(item.createTime) : '-',
                    mobile: item.mobile ? window.utils.formatMobileNumber(item.mobile) : '-',
                });
            });
            setTableData(list);
            setTotalRecord(+res.data.total);
            setTaskNum(window.utils.formatNum(res.data.total));
            setTaskTotal(res.data.metaData);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
            setFirstLoading(false);
        }
    }
    const handlePageChange = prop => {
        setListParams(state => ({ ...state, pageNum: prop }));
    };
    useEffect(() => {
        getCategory();
        getB2bCategory();
        getTaskerCategoryOptions();
    }, []);
    useEffect(() => {
        getTaskers();
    }, [listParams]);
    const dateOnChange = date => {
        setListParams({
            ...listParams,
            pageNum: 1,
            startTime: date ? dayjs(`${dayjs(date[0]).format('YYYY-MM-DD')} 00:00:00`).toISOString() : '',
            endTime: date ? dayjs(`${dayjs(date[1]).format('YYYY-MM-DD')} 23:59:59`).toISOString() : '',
        });
    };
    const handleChange = (value: string, item: defaultInfo) => {
        console.log(`selected ${value}`);
        const pat = {
            taskerId: item.taskerId,
            isDisable: value === 'Inactive',
        };
        if (value === 'Inactive') {
            setAccountModal({ open: true, disable: true, id: item.taskerId, type: 1 });
            return;
        }
        changeStuatus(pat);
    };
    const changeStuatus = async (pat: defaultInfo) => {
        try {
            const res = await api.changeTaskerStatus({ ...pat });
            getTaskers();
            setAccountModal({ open: false, disable: false, id: '', type: 1 });
        } catch (error: any) {
            message.error(error);
        }
    };
    const delTask = async (pat: defaultInfo) => {
        try {
            const res = await api.deleteTasker({ ...pat });
            getTaskers();
            setAccountModal({ open: false, disable: false, id: '', type: 1 });
        } catch (error: any) {
            message.error(error);
        }
    };
    const [accountModal, setAccountModal] = useState({ disable: false, open: false, id: '', type: 1 });
    const [visiblePop, setVisiblePop] = useState(-1);
    const { drop } = useAliveController();
    useActivate(() => {
        drop('TaskerDateil');
        // KeepAlive caches this page; refetch dropdown options when returning (e.g. after adding a tasker category).
        void (async () => {
            try {
                const res = await taskerCategoriesApi.getList({});
                setTaskerCategoryOptions(res.data ?? []);
            } catch (error: any) {
                message.error(error);
            }
        })();
    });
    const exportFileName = useMemo(() => {
        const { startTime, endTime } = listParams;
        if (!startTime || !endTime) {
            return 'Tasker List (All).csv';
        } else {
            return `Tasker List (${dayjs(startTime).format('MMMM D, YYYY')} - ${dayjs(endTime).format(
                'MMMM D, YYYY'
            )}).csv`;
        }
    }, [listParams]);
    const { downloadCSV } = useDownLoadCSV(exportFileName);

    const categoryNamesById = (ids: string[] | undefined, list: categoryType[]) =>
        (ids ?? [])
            .map(id => list.find(c => String(c.categoryId) === String(id))?.categoryName)
            .filter(Boolean)
            .join('; ');

    const handleExportCSV = async () => {
        const res = await api.getTaskerList({ ...listParams, pageNum: 1, pageSize: -1 });
        const data = res.data.records.map(item => {
            return {
                'Full Name': item.fullName,
                Email: item.email,
                Mobile: window.utils.formatMobileNumber(item.mobile),
                Gender: item.gender === 1 ? 'Male' : item.gender === 2 ? 'Female' : 'Non-Binary',
                'Tasks Completed': item.tasksCompleted,
                'Last Completed Task': format.customFormat(item.taskerClickCompletionTime, 'MMM DD YYYY'),
                'Last Online': format.customFormat(item.lastOnline, 'MMM DD YYYY'),
                'Market Categories': categoryNamesById(item.categoryIdList, category),
                'B2B Categories': categoryNamesById(item.b2bCategoryIdList, b2bCategory),
                'Tasker tier': item.taskerCategoryName ?? '',
            };
        });
        data.unshift({
            'Full Name': 'Full Name',
            Email: 'Email',
            Mobile: 'Mobile',
            Gender: 'Gender',
            'Tasks Completed': 'Tasks Completed',
            'Last Completed Task': 'Last Completed Task',
            'Last Online': 'Last Online',
            'Market Categories': 'Market Categories',
            'B2B Categories': 'B2B Categories',
            'Tasker tier': 'Tasker tier',
        });
        downloadCSV(data);
    };
    const [category, setCategory] = useState<categoryType[]>([]);
    const [taskerCategoryOptions, setTaskerCategoryOptions] = useState<TaskerCategoryOption[]>([]);

    const getTaskerCategoryOptions = async () => {
        try {
            const res = await taskerCategoriesApi.getList({
                pageNum: 1,
                pageSize: 500,
                keyword: '',
                startTime: '',
                endTime: '',
            });
            setTaskerCategoryOptions(res.data ?? []);
        } catch (error: any) {
            message.error(error);
        }
    };

    const handleTaskerCategoryChange = async (taskerId: string, value: string | null) => {
        try {
            await api.updateTaskerTaskerCategory(taskerId, value);
            message.success('Tasker tier updated');
            getTaskers();
        } catch (error: any) {
            message.error(error);
        }
    };

    const getCategory = async () => {
        try {
            const res = await categoriesApi.getCategoryList(listParams);
            setCategory(res.data);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    };
    const [b2bCategory, setB2bCategory] = useState<categoryType[]>([]);
    const getB2bCategory = async () => {
        try {
            const res = await b2bCategoriesApi.getCategoryList({ startTime: '', endTime: '', keyword: '' });
            setB2bCategory(res.data);
        } catch (error: any) {
            message.error(error);
        }
    };
    const handleMarketCategoriesChange = async (taskerId: string, categoryIds: string[]) => {
        setTableData(state =>
            state.map(row => (String(row.taskerId) === taskerId ? { ...row, categoryIdList: categoryIds } : row))
        );
        try {
            await api.updateTaskerMarketCategories(taskerId, categoryIds);
            message.success('Market categories updated');
        } catch (error: any) {
            message.error(error);
            getTaskers();
        }
    };
    const MAX_B2B_CATEGORIES = 6;
    const handleB2bCategoriesChange = async (taskerId: string, categoryIds: string[]) => {
        if (categoryIds.length > MAX_B2B_CATEGORIES) {
            message.warning(`A tasker can have at most ${MAX_B2B_CATEGORIES} B2B categories`);
            return;
        }
        setTableData(state =>
            state.map(row => (String(row.taskerId) === taskerId ? { ...row, b2bCategoryIdList: categoryIds } : row))
        );
        try {
            await api.updateTaskerB2bCategories(taskerId, categoryIds);
            message.success('B2B categories updated');
        } catch (error: any) {
            message.error(error);
            getTaskers();
        }
    };
    const popupContainer = document.getElementById('select-bar') || document.body;
    return (
        <>
            <div className="page-main account">
                <div className="page-title">Tasker</div>
                <div className="select-bar" id="select-bar">
                    <div className="select-bar-left">
                        <RangePicker
                            style={{ width: 'max-content' }}
                            className="search-input"
                            placeholder={['dd/mm/yyyy', 'dd/mm/yyyy']}
                            suffixIcon={calendarIcon}
                            separator={'-'}
                            format={'DD/MM/YYYY'}
                            disabledDate={disabledDate}
                            onChange={dateOnChange}
                            getPopupContainer={() => popupContainer}
                        />
                        <Select
                            defaultValue="All"
                            style={{ width: 150 }}
                            options={stateOptions}
                            suffixIcon={selectIcon}
                            getPopupContainer={() => popupContainer}
                            onChange={v => setListParams(state => ({ ...state, states: v, pageNum: 1 }))}
                        />
                        <Select
                            defaultValue="All"
                            style={{ width: 150 }}
                            options={onlineOptions}
                            suffixIcon={selectIcon}
                            getPopupContainer={() => popupContainer}
                            onChange={v => setListParams(state => ({ ...state, users: v, pageNum: 1 }))}
                        />
                        <Select
                            className="category-select"
                            defaultValue="All"
                            style={{ width: 150 }}
                            fieldNames={{ label: 'categoryName', value: 'categoryId' }}
                            options={[{ categoryName: 'All categories', categoryId: 'All' }, ...category]}
                            suffixIcon={selectIcon}
                            getPopupContainer={() => popupContainer}
                            onChange={v => setListParams(state => ({ ...state, categories: v, pageNum: 1 }))}
                        />
                        <Input
                            className="search-input"
                            style={{ maxWidth: 200 }}
                            placeholder="Search by name"
                            onChange={inputChange}
                            prefix={searchIcon}
                        />
                    </div>
                    <Button
                        type="primary"
                        size="small"
                        style={{
                            height: 46,
                            width: 128,
                            borderRadius: 8,
                            justifySelf: 'flex-end',
                        }}
                        onClick={handleExportCSV}
                    >
                        Export
                    </Button>
                </div>
                {!firstLoading ? (
                    <div className="page-total">
                        <div className="total-item">
                            Total number of Taskers<span>{taskNum ?? 0}</span>
                        </div>
                        <div className="total-item">
                            Total income amount<span>${taskTotal ? publicMethod.formatPrice(taskTotal) : '0.00'}</span>
                        </div>
                    </div>
                ) : (
                    ''
                )}
                {loading ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : (
                    <>
                        <div className="table tasker-table">
                            {totalRecord ? (
                                <>
                                    <div className="table-th">
                                        <Row>
                                            <Col flex={COLS.name}>Full Name</Col>
                                            <Col flex={COLS.email}>Email</Col>
                                            <Col flex={COLS.mobile}>Mobile</Col>
                                            <Col flex={COLS.tasks}>Tasks Completed</Col>
                                            <Col flex={COLS.lastTask}>Last completed Task</Col>
                                            <Col flex={COLS.lastOnline}>Last online</Col>
                                            <Col flex={COLS.market}>Market Category</Col>
                                            <Col flex={COLS.b2b}>B2B Category</Col>
                                            <Col flex={COLS.tier}>Tasker tier</Col>
                                            <Col flex={COLS.status}>Status</Col>
                                            <Col flex={COLS.action}></Col>
                                        </Row>
                                    </div>
                                    {tableData.map((item, index) => (
                                        <Row className="table-tr" key={`task-table-${index}`}>
                                            <Col
                                                flex={COLS.name}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    window.sessionStorage.setItem('taskerInfo', JSON.stringify(item));
                                                    link('/tasker/detail');
                                                }}
                                            >
                                                {item.fullName || '-'}
                                            </Col>
                                            <Col flex={COLS.email}>{item.email ?? '-'}</Col>
                                            <Col flex={COLS.mobile}>{item.mobile}</Col>
                                            <Col flex={COLS.tasks}>{item.tasksCompleted ?? '0'}</Col>
                                            <Col flex={COLS.lastTask}>
                                                {format.formatTimeMDY(item.taskerClickCompletionTime)}
                                            </Col>
                                            <Col flex={COLS.lastOnline}>{format.formatTimeMDY(item.lastOnline)}</Col>
                                            <Col
                                                flex={COLS.market}
                                                onClick={e => e.stopPropagation()}
                                                onMouseDown={e => e.stopPropagation()}
                                            >
                                                <CategoryMultiSelect
                                                    title="Market categories"
                                                    value={item.categoryIdList?.map(String) ?? []}
                                                    options={category}
                                                    onSave={ids =>
                                                        handleMarketCategoriesChange(String(item.taskerId), ids)
                                                    }
                                                    getPopupContainer={() => popupContainer}
                                                />
                                            </Col>
                                            <Col
                                                flex={COLS.b2b}
                                                onClick={e => e.stopPropagation()}
                                                onMouseDown={e => e.stopPropagation()}
                                            >
                                                <CategoryMultiSelect
                                                    title="B2B categories"
                                                    value={item.b2bCategoryIdList?.map(String) ?? []}
                                                    options={b2bCategory}
                                                    max={MAX_B2B_CATEGORIES}
                                                    onSave={ids =>
                                                        handleB2bCategoriesChange(String(item.taskerId), ids)
                                                    }
                                                    getPopupContainer={() => popupContainer}
                                                />
                                            </Col>
                                            <Col
                                                flex={COLS.tier}
                                                onClick={e => e.stopPropagation()}
                                                onMouseDown={e => e.stopPropagation()}
                                            >
                                                <Select
                                                    className="tasker-row-select"
                                                    popupClassName="tasker-row-dropdown"
                                                    placeholder="Select"
                                                    allowClear
                                                    style={{ width: '100%' }}
                                                    value={
                                                        item.taskerCategoryId != null && item.taskerCategoryId !== ''
                                                            ? String(item.taskerCategoryId)
                                                            : undefined
                                                    }
                                                    options={taskerCategoryOptions.map(o => ({
                                                        value: String(o.taskerCategoryId),
                                                        label: o.categoryName,
                                                    }))}
                                                    onChange={val =>
                                                        handleTaskerCategoryChange(
                                                            String(item.taskerId),
                                                            val === undefined || val === null ? null : String(val)
                                                        )
                                                    }
                                                    // Click the already-selected tier again to unselect it.
                                                    // (onChange doesn't fire when the value is unchanged.)
                                                    onSelect={val => {
                                                        const current =
                                                            item.taskerCategoryId != null &&
                                                            item.taskerCategoryId !== ''
                                                                ? String(item.taskerCategoryId)
                                                                : undefined;
                                                        if (String(val) === current) {
                                                            handleTaskerCategoryChange(String(item.taskerId), null);
                                                        }
                                                    }}
                                                    dropdownMatchSelectWidth={160}
                                                    suffixIcon={rowSelectArrow}
                                                    getPopupContainer={() => popupContainer}
                                                />
                                            </Col>
                                            <Col flex={COLS.status}>
                                                <Select
                                                    className={`tasker-row-select tasker-status-select ${
                                                        item.disabled ? 'is-inactive' : 'is-active'
                                                    }`}
                                                    popupClassName="tasker-row-dropdown"
                                                    defaultValue="Active"
                                                    style={{ width: '100%' }}
                                                    dropdownMatchSelectWidth={120}
                                                    onChange={val => {
                                                        handleChange(val, item);
                                                    }}
                                                    value={item.disabled ? 'Inactive' : 'Active'}
                                                    options={[
                                                        { value: 'Active', label: 'Active' },
                                                        { value: 'Inactive', label: 'Inactive' },
                                                    ]}
                                                    suffixIcon={rowSelectArrow}
                                                />
                                            </Col>
                                            <Col flex={COLS.action}>
                                                <Popover
                                                    open={visiblePop === index}
                                                    onOpenChange={v => setVisiblePop(v ? index : -1)}
                                                    placement="bottomRight"
                                                    content={
                                                        <div className="view-more">
                                                            <div
                                                                onClick={() => {
                                                                    setVisiblePop(-1);
                                                                    window.sessionStorage.setItem(
                                                                        'taskerInfo',
                                                                        JSON.stringify(item)
                                                                    );
                                                                    link('/tasker/detail');
                                                                }}
                                                            >
                                                                Details
                                                            </div>
                                                            <div
                                                                onClick={() => {
                                                                    setVisiblePop(-1);
                                                                    link('/user/chat');
                                                                    dispatch(
                                                                        setChatUser({
                                                                            chatUserId: item.taskerId,
                                                                            chatuserIdentity: 'Tasker',
                                                                            chatUserName: item.fullName,
                                                                        })
                                                                    );
                                                                }}
                                                            >
                                                                Tasker DM
                                                            </div>
                                                            <div
                                                                onClick={() => {
                                                                    setVisiblePop(-1);
                                                                    window.sessionStorage.setItem(
                                                                        'taskerInfo',
                                                                        JSON.stringify(item)
                                                                    );
                                                                    // Detail page reads this and opens straight into inline edit mode.
                                                                    window.sessionStorage.setItem(
                                                                        'taskerEditOpen',
                                                                        '1'
                                                                    );
                                                                    link('/tasker/detail');
                                                                }}
                                                            >
                                                                Edit
                                                            </div>
                                                            <div
                                                                onClick={() => {
                                                                    setVisiblePop(-1);
                                                                    setAccountModal({
                                                                        disable: false,
                                                                        open: true,
                                                                        id: item.taskerId,
                                                                        type: 2,
                                                                    });
                                                                }}
                                                            >
                                                                Delete
                                                            </div>
                                                        </div>
                                                    }
                                                >
                                                    <img
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            cursor: 'pointer',
                                                            textAlign: 'right',
                                                        }}
                                                        src={require('@/assets/images/view-more.png')}
                                                        alt=""
                                                    />
                                                </Popover>
                                            </Col>
                                        </Row>
                                    ))}
                                    <Pagination
                                        total={totalRecord}
                                        pageChange={handlePageChange}
                                        pageNo={listParams.pageNum}
                                    />
                                </>
                            ) : (
                                <div className="no-data flex-center">No data</div>
                            )}
                        </div>
                    </>
                )}
            </div>
            <Modal width={560} maskClosable={false} centered={true} open={accountModal.open} closeIcon={<></>}>
                <div className="modal-title">{accountModal.type === 1 ? 'Disabled account' : 'Delete account'}</div>
                <div className="modal-tip tip-font-20">
                    {accountModal.type === 1
                        ? 'Are you sure you want to disable this account?'
                        : 'Are you sure you want to delete this account?'}
                </div>
                <div className="flex-center" style={{ marginTop: 30, columnGap: 20 }}>
                    <Button
                        className="submit-btn"
                        onClick={() => {
                            setAccountModal({ disable: false, open: false, id: '', type: 1 });
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="submit-btn"
                        type="primary"
                        onClick={() => {
                            accountModal.type === 1
                                ? changeStuatus({
                                      taskerId: accountModal.id,
                                      isDisable: true,
                                  })
                                : delTask({ taskerId: accountModal.id });
                        }}
                    >
                        {accountModal.type === 1 ? 'Yes' : 'Yes, delete'}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default Tasker;
